import { useState } from "react"
import { formatDateTime } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Placeholder from "@/v2-ui/Placeholder"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Badge from "@/v2-backoffice-client/components/badge"
import Button from "@/v2-backoffice-client/components/button"
import { JsonDiffEditor } from "@/v2-backoffice-client/components/editor"
import { useLayerHistoryQuery } from "@/v2-backoffice-client/layer/history/layer.history.query"
import { useLayerUpdateMutation } from "@/v2-backoffice-client/layer/update/layer.update.mutation"
import { AUTHORING_MIRROR_ENVIRONMENT } from "@/v2-backoffice-client/feature/feature.constants"
import type { LayerHistoryQuery } from "@/v2-backoffice-client/layer/history/__types__/layer.history.query"

export type LayerHistoryListProps = {
  className?: string
  featureCode: string
}

type LayerVersion = NonNullable<
  LayerHistoryQuery["getStudioLayerHistory"]
>[number]

// The JSON blobs a revision snapshots — what the diff can show and what a
// restore writes back. Workflow state (enabled/review/priority) is owned
// by the toolbar and the order dialog, so restore leaves it alone.
const DIFF_FIELDS = [
  "Configuration",
  "Filters",
  "Interactions",
  "Export",
  "Table",
] as const

type DiffField = (typeof DIFF_FIELDS)[number]

function serialize(value: unknown): string {
  if (value == null) return ""
  return JSON.stringify(value, null, 2)
}

// Which snapshot fields differ between two revisions. Also checks the
// scalar extras so the per-revision chips can say "Thumbnail" even
// though only JSON fields are diffable in the editor.
function changedJsonFields(a: LayerVersion, b: LayerVersion): DiffField[] {
  return DIFF_FIELDS.filter(
    (field) => serialize(a[field]) !== serialize(b[field]),
  )
}

function changedChipLabels(version: LayerVersion, older: LayerVersion) {
  const labels: string[] = changedJsonFields(version, older)
  if ((version.Thumbnail ?? "") !== (older.Thumbnail ?? "")) {
    labels.push("Thumbnail")
  }
  if (version.Priority !== older.Priority) labels.push("Priority")
  if (version.IsEnabled !== older.IsEnabled) labels.push("Enabled")
  return labels
}

/**
 * Diff-first audit trail of the layer's catalog row (system-versioned,
 * newest first). Opens straight into "what changed in the last save":
 * the most recent previous revision is pre-selected and diffed against
 * the current one, field by field. Restoring a revision writes its
 * snapshot back through the regular save path — which creates a new
 * revision, so rolling forward is just restoring the newer one again.
 */
function LayerHistoryList(props: LayerHistoryListProps) {
  const { featureCode, className } = props
  const [selectedAt, setSelectedAt] = useState<string | null>(null)
  const [pickedField, setPickedField] = useState<DiffField | null>(null)

  const { data, loading, error } = useLayerHistoryQuery({
    variables: {
      input: {
        FeatureCode: featureCode,
        Environment: AUTHORING_MIRROR_ENVIRONMENT,
      },
    },
  })

  const [updateLayer, { loading: isRestoring }] = useLayerUpdateMutation({
    refetchQueries: ["LayerHistoryQuery", "LayerHistoryCountQuery"],
  })

  if (error) return <ErrorMessage error={error} />
  if (loading)
    return <Placeholder className="h-24">Loading history…</Placeholder>

  const versions = data?.getStudioLayerHistory ?? []
  const current = versions.find((version) => version.IsCurrent)

  // Diff-first: pre-select the most recent previous revision so the tab
  // answers "what changed last?" without any clicking.
  const effectiveSelectedAt =
    selectedAt ?? (versions[1] ? String(versions[1].ValidFrom) : null)
  const selected = versions.find(
    (version) =>
      String(version.ValidFrom) === effectiveSelectedAt && !version.IsCurrent,
  )

  const changedFields =
    selected && current ? changedJsonFields(selected, current) : []
  const diffField: DiffField =
    pickedField && changedFields.includes(pickedField)
      ? pickedField
      : (changedFields[0] ?? "Configuration")

  async function restore(version: LayerVersion) {
    await updateLayer({
      variables: {
        input: {
          FeatureCode: featureCode,
          Configuration: version.Configuration,
          Filters: version.Filters,
          Interactions: version.Interactions,
          Export: version.Export,
          Table: version.Table,
          Thumbnail: version.Thumbnail,
        },
      },
    })
  }

  return (
    <div className={cn("layer-history flex min-h-0 flex-1 gap-3", className)}>
      <div
        className="flex w-72 shrink-0 flex-col gap-1 overflow-y-auto"
        role="list"
      >
        {versions.map((version, index) => {
          const isSelected =
            !version.IsCurrent &&
            String(version.ValidFrom) === effectiveSelectedAt
          const older = versions[index + 1]
          const chips = older ? changedChipLabels(version, older) : ["Created"]
          return (
            <button
              key={String(version.ValidFrom)}
              type="button"
              role="listitem"
              disabled={version.IsCurrent}
              data-feature-code="UI_BackofficeLayers_History_SelectVersion"
              className={cn(
                "rounded-md border p-2 text-left text-xs",
                isSelected && "border-primary",
                version.IsCurrent && "cursor-default bg-muted/30",
              )}
              onClick={() => {
                setSelectedAt(String(version.ValidFrom))
                setPickedField(null)
              }}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium">
                  {formatDateTime(new Date(String(version.ValidFrom)))}
                </span>
                {version.IsCurrent && <Badge variant="positive">Current</Badge>}
              </span>
              <span className="mt-1 flex flex-wrap gap-1">
                {chips.length === 0 ? (
                  <span className="text-muted-foreground">
                    No content changes
                  </span>
                ) : (
                  chips.map((chip) => (
                    <span
                      key={chip}
                      className="rounded border bg-muted/40 px-1 py-0.5 text-[10px] text-muted-foreground"
                    >
                      {chip}
                    </span>
                  ))
                )}
              </span>
              {version.UserId != null && (
                <span className="mt-1 block text-muted-foreground">
                  by user #{version.UserId}
                </span>
              )}
            </button>
          )
        })}
        {versions.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No history yet — the first save of this layer starts its audit
            trail.
          </p>
        )}
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
        {selected && current ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <div
                className="flex items-center gap-1"
                role="toolbar"
                aria-label="Changed fields"
              >
                {(changedFields.length > 0
                  ? changedFields
                  : (["Configuration"] as DiffField[])
                ).map((field) => (
                  <Button
                    key={field}
                    size="sm"
                    variant={field === diffField ? "secondary" : "ghost"}
                    aria-pressed={field === diffField}
                    data-feature-code="UI_BackofficeLayers_History_PickDiffField"
                    onClick={() => setPickedField(field)}
                  >
                    {field}
                  </Button>
                ))}
              </div>
              <span className="ml-auto text-xs text-muted-foreground">
                Selected revision on the left, current on the right.
              </span>
            </div>

            {changedFields.length === 0 && (
              <p className="text-xs text-muted-foreground">
                This revision's content matches the current one — only metadata
                (thumbnail, priority or enabled state) differed.
              </p>
            )}

            <div className="min-h-[300px] flex-1 overflow-hidden rounded-md border">
              <JsonDiffEditor
                original={serialize(selected[diffField])}
                modified={serialize(current[diffField])}
              />
            </div>

            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                iconSrc="time-past"
                isLoading={isRestoring}
                data-feature-code="UI_BackofficeLayers_History_Restore"
                onClick={() => void restore(selected)}
              >
                Restore this revision
              </Button>
              <span className="text-xs text-muted-foreground">
                Restoring saves this snapshot as a new revision (nothing is lost
                — restore the newer one to roll forward). Live state and draw
                order are not touched.
              </span>
            </div>
          </>
        ) : (
          <Placeholder className="h-24">
            {versions.length < 2
              ? "Only one revision exists — edits made in the other tabs will show up here as new revisions to compare and restore."
              : "Select a previous revision to diff it against the current one."}
          </Placeholder>
        )}
      </div>
    </div>
  )
}

export default LayerHistoryList
